import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Search, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api, type ApiResponse } from "@/api/client";
import { sortBySearchMatch } from "@ckgo2/shared";
import { formatDate } from "@/lib/format";

interface NewsletterSubscriber {
  id: string;
  email: string;
  name?: string;
  source: string;
  // Derived server-side (admin/newsletter.ts) from isActive + unsubscribedAt + the consent ledger.
  status: "active" | "unsubscribed" | "suppressed" | "pending";
  createdAt: string;
}

/** Subscribers tab (moved from the retired /newsletter page — block 4.15 řez 3). */
export default function SubscribersPanel() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["newsletter-subscribers"],
    queryFn: () =>
      api.get<ApiResponse<NewsletterSubscriber[]>>("/api/admin/newsletter/subscribers"),
  });

  const subscribers = data?.data ?? [];
  // Summary counts in one pass (each status drives only a `.length` in the header).
  const counts = subscribers.reduce<Record<string, number>>((m, s) => {
    m[s.status] = (m[s.status] ?? 0) + 1;
    return m;
  }, {});
  // Hledání filtruje i řadí podle shody s e-mailem (první sloupec), shoda jen ve jménu jde za ní.
  const filtered = sortBySearchMatch(subscribers, search, {
    name: (s) => s.email,
    other: (s) => [s.name],
    tieBreak: () => 0,
  });

  if (isLoading) {
    return (
      <div className="flex h-40 items-center justify-center text-gray-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="card space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-600">
          <strong>{counts.active ?? 0}</strong>{" "}
          {t("newsletter.subscribers.activeWord", { count: counts.active ?? 0 })} ·{" "}
          {counts.unsubscribed ?? 0}{" "}
          {t("newsletter.subscribers.unsubscribedWord", { count: counts.unsubscribed ?? 0 })}
          {(counts.suppressed ?? 0) > 0 && (
            <>
              {" "}
              · {counts.suppressed}{" "}
              {t("newsletter.subscribers.suppressedWord", { count: counts.suppressed })}
            </>
          )}
          {(counts.pending ?? 0) > 0 && (
            <>
              {" "}
              · {counts.pending}{" "}
              {t("newsletter.subscribers.pendingWord", { count: counts.pending })}
            </>
          )}
        </p>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder={t("newsletter.subscribers.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-9"
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="flex h-32 flex-col items-center justify-center text-gray-500">
          <Users className="mb-2 h-8 w-8 text-gray-300" />
          <p>{t("newsletter.subscribers.noSubscribers")}</p>
        </div>
      ) : (
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="pb-3 text-left text-sm font-medium text-gray-500">
                {t("common.fields.email")}
              </th>
              <th className="pb-3 text-left text-sm font-medium text-gray-500">
                {t("common.fields.name")}
              </th>
              <th className="pb-3 text-left text-sm font-medium text-gray-500">
                {t("newsletter.subscribers.colSource")}
              </th>
              <th className="pb-3 text-left text-sm font-medium text-gray-500">
                {t("newsletter.subscribers.colSubscribed")}
              </th>
              <th className="pb-3 text-left text-sm font-medium text-gray-500">
                {t("common.fields.status")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filtered.map((s) => (
              <tr key={s.id}>
                <td className="py-3 text-gray-900">{s.email}</td>
                <td className="py-3 text-gray-600">{s.name || "-"}</td>
                <td className="py-3">
                  <span className="badge text-xs">{s.source}</span>
                </td>
                <td className="py-3 text-sm text-gray-500">{formatDate(s.createdAt)}</td>
                <td className="py-3">
                  {s.status === "active" ? (
                    <span className="badge bg-success-100 text-success-700">
                      {t("common.status.active")}
                    </span>
                  ) : s.status === "suppressed" ? (
                    <span className="badge bg-amber-100 text-amber-700">
                      {t("newsletter.subscribers.statusSuppressed")}
                    </span>
                  ) : s.status === "pending" ? (
                    <span className="badge bg-blue-100 text-blue-700">
                      {t("newsletter.subscribers.statusPending")}
                    </span>
                  ) : (
                    <span className="badge bg-gray-100 text-gray-600">
                      {t("newsletter.subscribers.statusUnsubscribed")}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
